import React from 'react';
import { PredictionSet } from '../types';
import { motion } from 'framer-motion';
import Tooltip from './Tooltip';
import { Share2, Trophy, Info } from 'lucide-react';
import LottoBall from './LottoBall';
import { STRATEGY_HELP } from './ActionButtons';

interface PredictionCardProps {
  prediction: PredictionSet;
  index: number;
  onShare: () => void;
}

const PredictionCard: React.FC<PredictionCardProps> = ({ prediction, index, onShare }) => {
  const checked = Boolean(prediction.resultChecked);
  const mainMatchCount = prediction.matchedNumbers?.length || 0;
  const powerballMatch = Boolean(prediction.powerballMatch);
  const fullMainMatch = Boolean(prediction.fullMainMatch || mainMatchCount === 5);
  const fullMatchWithPowerball = Boolean(prediction.fullMatchWithPowerball || (fullMainMatch && powerballMatch));
  const hasAnyMatch = checked && (mainMatchCount > 0 || powerballMatch);

  const cardState = fullMainMatch
    ? 'border-yellow-300 bg-yellow-950/30 shadow-[0_0_34px_rgba(250,204,21,0.24)] ring-2 ring-yellow-400/40 animate-pulse'
    : hasAnyMatch
      ? 'border-emerald-500/70 bg-emerald-950/15 shadow-[0_0_24px_rgba(16,185,129,0.16)] ring-1 ring-emerald-400/30 animate-pulse'
      : 'border-slate-700 hover:border-yellow-500/50';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15, duration: 0.5 }}
      className={`bg-slate-800/40 backdrop-blur-sm border p-5 rounded-xl transition-all duration-300 group print-container relative ${cardState}`}
    >
      <div className="flex justify-between items-start gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-yellow-500 text-sm md:text-base uppercase tracking-wider">{prediction.strategy}</h3>
            {prediction.strategy in STRATEGY_HELP && (
              <Tooltip text={STRATEGY_HELP[prediction.strategy as keyof typeof STRATEGY_HELP]}>
                <span className="text-slate-500 hover:text-yellow-400 cursor-help"><Info size={14} /></span>
              </Tooltip>
            )}
          </div>
          {prediction.targetDrawNumber && (
            <div className="text-[11px] text-slate-500 mt-1">
              Target Draw #{prediction.targetDrawNumber}{prediction.targetDrawDate ? ` • ${prediction.targetDrawDate}` : ''}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Tooltip text="A heuristic pattern-fit score based on historical alignment and balance. It is not the probability of winning.">
            <div className="text-xs font-mono text-slate-500 bg-slate-900 px-2 py-1 rounded cursor-help">
              Confidence: <span className="text-green-400">{prediction.confidence}%</span>
            </div>
          </Tooltip>
          <button onClick={onShare} className="text-slate-500 hover:text-yellow-500 transition-colors p-1 rounded-full hover:bg-slate-700/50" title="Share this prediction">
            <Share2 size={16} />
          </button>
        </div>
      </div>

      {checked && (
        <div className={`mb-4 px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 ${fullMainMatch ? 'bg-yellow-950/60 text-yellow-200 border border-yellow-500/60' : hasAnyMatch ? 'bg-emerald-950/50 text-emerald-300 border border-emerald-700/50' : 'bg-slate-900/60 text-slate-400 border border-slate-700'}`}>
          {(hasAnyMatch || fullMainMatch) && <Trophy size={15} />}
          {fullMainMatch
            ? <span className="font-black uppercase tracking-wider">Winner — 5/5 main numbers{fullMatchWithPowerball ? ' + Power Ball' : ''}</span>
            : hasAnyMatch
              ? `${mainMatchCount}/5 main number${mainMatchCount === 1 ? '' : 's'} matched${powerballMatch ? ' + Power Ball' : ''}`
              : 'Result checked — no matches in this prediction set'}
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center gap-2">
        <div className="flex flex-wrap gap-2 md:gap-3">
          {prediction.numbers.map((num, idx) => (
            <LottoBall key={idx} number={num} size="md" />
          ))}
        </div>
        <LottoBall number={prediction.powerball} powerball size="md" title="Power Ball" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] text-slate-500 font-mono">
        <div className="bg-slate-900/60 rounded px-2 py-1 text-center">SUM <span className="text-slate-300 font-bold">{prediction.numbers.reduce((a, b) => a + b, 0)}</span></div>
        <div className="bg-slate-900/60 rounded px-2 py-1 text-center">LOW/HIGH <span className="text-slate-300 font-bold">{prediction.numbers.filter(n => n <= 17).length}/{prediction.numbers.filter(n => n >= 18).length}</span></div>
        <div className="bg-slate-900/60 rounded px-2 py-1 text-center">ODD/EVEN <span className="text-slate-300 font-bold">{prediction.numbers.filter(n => n % 2).length}/{prediction.numbers.filter(n => n % 2 === 0).length}</span></div>
      </div>
    </motion.div>
  );
};

export default PredictionCard;
